import { useState, useEffect, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Consent Dashboard Tile
 * --------------------------------------------------------------------------
 * Compact live-stats tile showing the health of the Trade-Vendor Introduction
 * Service consent funnel. Two render modes:
 *   - compact (default): for home dashboard — single card with counts
 *   - expanded: for Vendor Network tab header — also shows awaiting list +
 *               recent activity ticker
 *
 * Click-through navigates to the Vendor Network tab filtered by status.
 */
const ConsentDashboardTile = ({ mode = "compact", setTab, onJumpToInvited }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendors/consent-stats`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setStats(d);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div data-testid="consent-tile-loading" style={tileShell}>
        <p style={{ color: "#ADBED1", fontSize: 13, margin: 0 }}>Loading consent stats...</p>
      </div>
    );
  }

  if (!stats) return null;

  const { counts, rates } = stats;
  const totalSent = counts.total_sent || 0;

  // Empty state — Shannon hasn't sent any invitations yet
  if (totalSent === 0) {
    const openVendorInvite = () => {
      if (!setTab) return;
      // Signal the VendorNetworkTab to auto-open the Add Vendor modal
      // on mount so the CEO's next tap is inside the invitation form,
      // not the tab landing page.
      try { sessionStorage.setItem("mf_vendor_open_invite", "1"); } catch (_) { /* ignore */ }
      setTab("vendor-network");
    };
    return (
      <div data-testid="consent-tile-empty" style={tileShell}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 30 }}>🤝</div>
          <div style={{ flex: 1 }}>
            <h3 style={{ color: "#fff", fontSize: 14, margin: 0, fontWeight: 800 }}>
              Trade-Vendor Consent Funnel
            </h3>
            <p style={{ color: "#ADBED1", fontSize: 12, margin: "3px 0 0", lineHeight: 1.4 }}>
              No invitations sent yet. Select the &apos;+ Add Vendor&apos; button below to dispatch an invitation and begin building your opted-in network.
            </p>
          </div>
          <button
            onClick={openVendorInvite}
            data-testid="add-vendor-btn"
            style={ctaButton}>
            + Add Vendor
          </button>
        </div>
      </div>
    );
  }

  const handleResend = async (vendor_id) => {
    if (!window.confirm("Send a follow-up invitation to this vendor?")) return;
    setResending(prev => ({ ...prev, [vendor_id]: true }));
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendors/${vendor_id}/send-invitation`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ base_url: window.location.origin }),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        await load();
      } else {
        alert(d.detail || d.error || "Could not resend");
      }
    } catch {
      alert("Could not resend");
    } finally {
      setResending(prev => ({ ...prev, [vendor_id]: false }));
    }
  };

  return (
    <div data-testid={`consent-tile-${mode}`} style={tileShell}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 26 }}>🤝</div>
          <div>
            <h3 style={{ color: "#fff", fontSize: 14, margin: 0, fontWeight: 800 }}>Trade-Vendor Consent Funnel</h3>
            <p style={{ color: "#ADBED1", fontSize: 11, margin: "2px 0 0" }}>
              {totalSent} invitation{totalSent === 1 ? "" : "s"} sent · {rates.response_rate_pct}% response · {rates.opt_in_rate_pct}% opt-in
            </p>
          </div>
        </div>
        {mode === "compact" && setTab && (
          <button onClick={() => setTab("vendor-network")} data-testid="consent-tile-cta" style={ctaButton}>
            Manage →
          </button>
        )}
      </div>

      {/* Stat tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 14 }}>
        <Stat label="Invited" value={counts.invited} color="#F59E0B" testId="stat-invited" onClick={onJumpToInvited} />
        <Stat label="Opted In" value={counts.opted_in} color="#A78BFA" testId="stat-opted-in" badge={counts.opted_in > 0 ? "🤝" : null} />
        <Stat label="Opted Out" value={counts.opted_out} color="#94A3B8" testId="stat-opted-out" />
        <Stat label="Stale" value={counts.stale_awaiting} color={counts.stale_awaiting > 0 ? "#EF4444" : "#64748B"} testId="stat-stale" hint=">7d no reply" />
      </div>

      {/* Expanded mode: awaiting list + activity ticker */}
      {mode === "expanded" && (
        <>
          {stats.awaiting.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <h4 style={sectionTitle}>
                ⏳ Awaiting Response ({stats.awaiting.length})
              </h4>
              <p style={{ color: "#7E92A8", fontSize: 11, margin: "0 0 10px" }}>
                Re-send a friendly follow-up to anyone sitting &gt;7 days.
              </p>
              <div style={{ maxHeight: 240, overflowY: "auto" }}>
                {stats.awaiting.map(a => (
                  <div key={a.consent_id} data-testid={`awaiting-${a.consent_id}`} style={{
                    padding: "9px 11px", marginBottom: 6, borderRadius: 6,
                    background: a.is_stale ? "rgba(239,68,68,0.06)" : "rgba(255,255,255,0.025)",
                    border: `1px solid ${a.is_stale ? "rgba(239,68,68,0.25)" : "rgba(255,255,255,0.08)"}`,
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap",
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ color: "#fff", fontSize: 12.5, fontWeight: 700, margin: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {a.vendor_name}
                      </p>
                      <p style={{ color: a.is_stale ? "#FCA5A5" : "#7E92A8", fontSize: 10.5, margin: "2px 0 0" }}>
                        {a.vendor_email} · invited {a.days_since}d ago {a.is_stale && "· STALE"}
                      </p>
                    </div>
                    <button
                      onClick={() => handleResend(a.vendor_id)}
                      disabled={resending[a.vendor_id]}
                      data-testid={`resend-${a.consent_id}`}
                      style={{
                        background: "rgba(167,139,250,0.15)", color: "#A78BFA",
                        border: "1px solid #A78BFA", borderRadius: 5, padding: "5px 10px",
                        fontSize: 11, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap",
                        opacity: resending[a.vendor_id] ? 0.5 : 1,
                      }}
                    >
                      {resending[a.vendor_id] ? "..." : "📨 Re-send"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats.recent_activity.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <h4 style={sectionTitle}>📰 Recent Activity</h4>
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {stats.recent_activity.map((a, i) => (
                  <li key={i} style={{ padding: "6px 0", borderBottom: i < stats.recent_activity.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none", fontSize: 12 }}>
                    {a.status === "opted_in" ? (
                      <span><span style={{ color: "#A78BFA" }}>🤝 {a.vendor_name}</span> <span style={{ color: "#7E92A8" }}>opted in</span></span>
                    ) : (
                      <span><span style={{ color: "#94A3B8" }}>{a.vendor_name}</span> <span style={{ color: "#7E92A8" }}>declined</span></span>
                    )}
                    <span style={{ color: "#64748B", fontSize: 10, marginLeft: 6 }}>
                      {a.responded_at ? new Date(a.responded_at).toLocaleDateString() : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Stat = ({ label, value, color, testId, badge, hint, onClick }) => (
  <div
    data-testid={testId}
    onClick={onClick}
    style={{
      padding: "10px 8px", background: "rgba(255,255,255,0.03)",
      border: `1px solid ${color}40`, borderRadius: 8, textAlign: "center",
      cursor: onClick ? "pointer" : "default",
    }}
  >
    <p style={{ color, fontSize: 18, fontWeight: 800, margin: 0 }}>
      {badge && <span style={{ marginRight: 4 }}>{badge}</span>}{value}
    </p>
    <p style={{ color: "#7E92A8", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", margin: "2px 0 0" }}>
      {label}
    </p>
    {hint && (
      <p style={{ color: "#64748B", fontSize: 9, margin: "1px 0 0", fontStyle: "italic" }}>{hint}</p>
    )}
  </div>
);

const tileShell = {
  padding: "14px 18px",
  background: "linear-gradient(135deg, rgba(167,139,250,0.06) 0%, rgba(0,255,255,0.04) 100%)",
  border: "1px solid rgba(167,139,250,0.25)",
  borderRadius: 12,
  marginBottom: 16,
};
const ctaButton = {
  background: "rgba(167,139,250,0.15)", color: "#A78BFA",
  border: "1px solid #A78BFA", borderRadius: 6, padding: "6px 12px",
  fontSize: 11, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap",
};
const sectionTitle = { color: "#fff", fontSize: 12, fontWeight: 800, margin: "0 0 6px", letterSpacing: "-0.01em" };

export default ConsentDashboardTile;
